/**
 * kHashRouter — a reusable single-page-app hash router (the browser shell around the pure
 * kHashRoute helpers). It is the single source of truth for "which screen is shown": every
 * navigable surface has a direct, bookmarkable `#` link, and the browser Back/Forward
 * buttons move between screens because each navigation is a distinct URL-hash history entry.
 *
 * A consumer sets KnownSurfaces (its top-level route names), subscribes with Start(OnRoute),
 * and navigates with Go(...) — which only SETS the hash. The one hashchange listener drives
 * the single apply path (OnRoute), so a click and a Back button flow through identical code.
 *
 * Modal history (OpenModal/CloseModal): a modal (detail dialog / overlay) is NOT a routed
 * screen — it is a transient layer over the current one — but it should still be dismissable
 * with the browser Back button. So OpenModal pushes a history entry (same URL, no hashchange)
 * and registers the modal's close callback; a Back pops that entry (popstate) and the router
 * closes the top modal; closing the modal by its own UI pops the entry so URL and stack stay
 * in step. A real navigation (hashchange) dismisses any open modal. This is generic — a
 * kModalDialog wires its Show/Close to the STATIC OpenModal/CloseModal (via the active router),
 * and any custom overlay can call the same two statics.
 */

import ks from "./kBrowserShared";
import { IkHashParams, IkHashRoute, kParseHash, kBuildHash, kCanonicalHash } from "../kReusableCoreTS/kHashRoute";

export type { IkHashParams, IkHashRoute } from "../kReusableCoreTS/kHashRoute";

export class kHashRouter {
    /** The top-level route names this app recognises (anything else parses to the root).
     *  Set by the consumer before Start(). */
    public KnownSurfaces: string[] = [];

    private _OnRoute: ((Route: IkHashRoute) => void) | null = null;
    /** Close callbacks for the currently-open modals (top = most recently opened). */
    private _ModalClosers: Array<() => void> = [];
    /** True while the router is closing a modal in response to a Back/navigation (the browser
     *  already moved the history), so CloseModal must NOT push another history.back(). */
    private _SuppressModalPop: boolean = false;
    /** UI closes whose history.back() has not arrived as a popstate yet (it is asynchronous). */
    private _BacksPending: number = 0;
    /** Modals opened while a back was pending: their pushState waits for that popstate, or the
     *  late back would pop the new entry (close one modal and open the next in the same tap). */
    private _PushesQueued: number = 0;
    /** The router that owns the page history — the target of the STATIC modal helpers so any
     *  modal/overlay can reach it without being handed the instance. */
    private static _Active: kHashRouter | null = null;

    /** Register the apply callback, become the active router, attach the hashchange +
     *  popstate listeners, normalise a non-canonical boot hash (e.g. "#/garbage" → "#/")
     *  without adding history, then apply the current hash once so a deep link loads straight
     *  into its screen. */
    public Start = (OnRoute: (Route: IkHashRoute) => void): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._OnRoute = OnRoute;
            kHashRouter._Active = this;
            window.addEventListener("hashchange", this._OnHashChange);
            window.addEventListener("popstate", this._OnPopState);
            this._NormaliseBootHash();
            this._Dispatch();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.Start");
        }
    };

    /** Parse the live URL hash into a route. */
    public Current = (): IkHashRoute => {
        if (ks.KErrorMode === true) { return { Surface: "", Sub: "", Arg: "" }; }
        try {
            return kParseHash(this._Raw(), this.KnownSurfaces);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.Current");
            return { Surface: "", Sub: "", Arg: "" };
        }
    };

    /** Navigate to a screen by setting the URL hash (pushes a history entry). No-op when the
     *  target equals the current canonical hash, so re-selecting the active screen neither
     *  loops nor pollutes the Back stack. The hashchange listener does the apply. */
    // DOCUMENTED EXCEPTION: pure hash computation, no I/O; the Go/GoReplace
    // gate+try/catch and per-member labels stay with the callers.
    private _TargetFor = (Surface: string, Sub?: string, Arg?: string, Params?: IkHashParams): string => {
        let SubValue: string = "";
        if (Sub !== undefined) { SubValue = Sub; }
        let ArgValue: string = "";
        if (Arg !== undefined) { ArgValue = Arg; }
        const Route: IkHashRoute = { Surface: Surface, Sub: SubValue, Arg: ArgValue };
        if (Params !== undefined) { Route.Params = Params; }
        return kBuildHash(Route, this.KnownSurfaces);
    };

    /** Params (optional): the "?key=value" query of the target ("#/search?q=x"). */
    public Go = (Surface: string, Sub?: string, Arg?: string, Params?: IkHashParams): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Target: string = this._TargetFor(Surface, Sub, Arg, Params);
            if (kCanonicalHash(this._Raw(), this.KnownSurfaces) === Target) { return; }
            window.location.hash = Target;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.Go");
        }
    };

    /** Navigate to a screen REPLACING the current history entry (no new entry) AND apply it.
     *  Use for a "the previous screen is done, don't keep it in the Back stack" transition —
     *  e.g. a form → its created record's detail: Back from the detail should return to where
     *  the form was opened FROM, not re-open the submitted form. Deterministic: replaceState
     *  (which fires no event) then an explicit dispatch, so the apply never depends on whether
     *  a fragment change raised hashchange. No-op when already on the target canonical hash. */
    public GoReplace = (Surface: string, Sub?: string, Arg?: string, Params?: IkHashParams): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Target: string = this._TargetFor(Surface, Sub, Arg, Params);
            if (kCanonicalHash(this._Raw(), this.KnownSurfaces) === Target) { return; }
            window.history.replaceState(null, "", Target);
            this._Dispatch();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.GoReplace");
        }
    };

    /** Canonicalise the CURRENT URL in place (replaceState — no new history entry, no
     *  dispatch) to a corrected route. Use when the shown screen is already right but the
     *  URL named something that resolved elsewhere (e.g. a stale deep link to an
     *  empty sub-selection that fell back to a default), so Back does not return to the
     *  stale entry. No-op when already canonical. */
    public Replace = (Surface: string, Sub?: string, Arg?: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let SubValue: string = "";
            if (Sub !== undefined) { SubValue = Sub; }
            let ArgValue: string = "";
            if (Arg !== undefined) { ArgValue = Arg; }
            const Target: string = kBuildHash({ Surface: Surface, Sub: SubValue, Arg: ArgValue }, this.KnownSurfaces);
            if (kCanonicalHash(this._Raw(), this.KnownSurfaces) === Target) { return; }
            window.history.replaceState(null, "", Target);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.Replace");
        }
    };

    /** Step one entry back in the browser history — the in-app "back" that mirrors the
     *  browser Back button exactly. */
    public Back = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            window.history.back();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.Back");
        }
    };

    /** For an app that keeps its OWN hash routing (its own hashchange listener, routes kParseHash does not
     *  know): become the active router for the modal Back stack ONLY - the popstate and hashchange
     *  listeners, but no boot-hash normalising (it would rewrite the app's routes to "#/") and no route
     *  dispatch. Idempotent; a router already Started is left as it is. */
    public static StartModalsOnly(): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kHashRouter._Active !== null) { return; }
            const Router: kHashRouter = new kHashRouter();
            kHashRouter._Active = Router;
            window.addEventListener("hashchange", Router._OnHashChange);
            window.addEventListener("popstate", Router._OnPopState);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.StartModalsOnly");
        }
    }

    /** Every native <dialog> under Root that opens (show / showModal) is registered with OpenModal, so the
     *  browser Back button - a phone's Back gesture - closes it instead of leaving the screen; closing it by
     *  its own UI (button, Escape, backdrop) pops that history entry. Needs an active router (Start or
     *  StartModalsOnly). Idempotent per Root. */
    public static WatchDialogs(Root: Document): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kDialogBackWatch.Watched.has(Root) === true) { return; }
            kDialogBackWatch.Watched.add(Root);
            const Watch: kDialogBackWatch = new kDialogBackWatch();
            const Observer: MutationObserver = new MutationObserver(Watch.OnMutations);
            Observer.observe(Root.documentElement, { subtree: true, attributes: true, attributeFilter: ["open"] });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.WatchDialogs");
        }
    }

    // ── Modal history (static entry points → the active router) ──────────────────────

    /** Register an opening modal so the browser Back button (and a subsequent navigation)
     *  will close it. Pass the modal's own close function. */
    public static OpenModal(Close: () => void): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kHashRouter._Active !== null) { kHashRouter._Active._OpenModalInstance(Close); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.OpenModal");
        }
    }

    /** Called from a modal's own close path (×, Cancel, OK, Escape, backdrop) so the pushed
     *  history entry is popped and the URL/stack stay in step. */
    public static CloseModal(): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kHashRouter._Active !== null) { kHashRouter._Active._CloseModalInstance(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter.CloseModal");
        }
    }

    private _OpenModalInstance = (Close: () => void): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Depth: number = this._ModalClosers.length + 1;
            this._ModalClosers.push(Close);
            if (this._BacksPending > 0) { this._PushesQueued = this._PushesQueued + 1; return; }
            // Same URL, no hashchange — the surface underneath does not re-apply; only the
            // history depth grows, so Back pops back to the surface and closes the modal.
            window.history.pushState({ kModalDepth: Depth }, "");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._OpenModalInstance");
        }
    };

    private _CloseModalInstance = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this._ModalClosers.length === 0) { return; }
            this._ModalClosers.pop();
            // When the close was triggered by a Back/navigation the browser already moved the
            // history — do NOT push another back (that would over-pop). A UI-initiated close
            // pops the entry it pushed.
            if (this._SuppressModalPop === true) { return; }
            if (this._PushesQueued > 0) { this._PushesQueued = this._PushesQueued - 1; return; }   // its entry was never pushed
            this._BacksPending = this._BacksPending + 1;
            window.history.back();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._CloseModalInstance");
        }
    };

    // ── Internals ────────────────────────────────────────────────────────────────────

    private _OnHashChange = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            // A real navigation dismisses any open modal, then applies the new route.
            this._CloseAllModals();
            this._Dispatch();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._OnHashChange");
        }
    };

    private _OnPopState = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            // A Back/Forward that did NOT change the hash (a modal history entry) reconciles the
            // open-modal stack to the target entry's depth. A hash-changing Back also fires
            // hashchange, which closes any remaining modals + applies the route.
            if (this._BacksPending > 0) { this._OnOwnBack(); return; }
            this._ReconcileModals();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._OnPopState");
        }
    };

    // The popstate of a UI close's own history.back(): the stack already matches; the modals
    // opened meanwhile get their entries now.
    private _OnOwnBack = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._BacksPending = this._BacksPending - 1;
            if (this._BacksPending > 0) { return; }
            let Depth: number = this._ModalClosers.length - this._PushesQueued;
            while (this._PushesQueued > 0) {
                this._PushesQueued = this._PushesQueued - 1;
                Depth = Depth + 1;
                window.history.pushState({ kModalDepth: Depth }, "");
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._OnOwnBack");
        }
    };

    private _CloseAllModals = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let Guard: number = 0;
            while (this._ModalClosers.length > 0 && Guard < 100) {
                Guard = Guard + 1;
                this._CloseTopModal();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._CloseAllModals");
        }
    };

    private _ReconcileModals = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const StateDepth: number = this._StateDepth();
            let Guard: number = 0;
            while (this._ModalClosers.length > StateDepth && Guard < 100) {
                Guard = Guard + 1;
                this._CloseTopModal();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._ReconcileModals");
        }
    };

    /** Close the top modal WITHOUT triggering a history.back (the browser has already moved,
     *  or we are closing-all on a navigation). Its close routes through _CloseModalInstance
     *  (suppressed), which pops the stack; if a closer does not, we force-pop so the loop ends. */
    private _CloseTopModal = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Before: number = this._ModalClosers.length;
            if (Before === 0) { return; }
            const Top: () => void = this._ModalClosers[Before - 1];
            this._SuppressModalPop = true;
            Top();
            this._SuppressModalPop = false;
            if (this._ModalClosers.length === Before) { this._ModalClosers.pop(); }
        } catch (e) {
            this._SuppressModalPop = false;
            ks.KGlobalErrorHandler(e, "kHashRouter._CloseTopModal");
        }
    };

    private _StateDepth = (): number => {
        if (ks.KErrorMode === true) { return 0; }
        try {
            const State: unknown = window.history.state;
            if (State !== null && typeof State === "object") {
                const Depth: unknown = (State as { kModalDepth?: unknown }).kModalDepth;
                if (typeof Depth === "number") { return Depth; }
            }
            return 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._StateDepth");
            return 0;
        }
    };

    private _Dispatch = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this._OnRoute === null) { return; }
            this._OnRoute(this.Current());
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._Dispatch");
        }
    };

    private _Raw = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (typeof window === "undefined") { return ""; }
            return window.location.hash;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._Raw");
            return "";
        }
    };

    /** Replace a non-canonical boot hash in place (no history entry, no dispatch) so the URL
     *  always reflects the shown screen. Leaves an empty hash empty (the clean root). */
    private _NormaliseBootHash = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Raw: string = this._Raw();
            if (Raw === "") { return; }
            const Canonical: string = kCanonicalHash(Raw, this.KnownSurfaces);
            if (Canonical === Raw) { return; }
            window.history.replaceState(null, "", Canonical);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHashRouter._NormaliseBootHash");
        }
    };
}

/** kHashRouter.WatchDialogs' observer: a <dialog> that gained `open` gets a kDialogBackEntry. */
class kDialogBackWatch {
    public static Watched: WeakSet<Document> = new WeakSet<Document>();
    private readonly Open: WeakSet<Element> = new WeakSet<Element>();

    public OnMutations = (Records: MutationRecord[]): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            for (let i = 0; i < Records.length; i++) {
                const El: Node = Records[i].target;
                if (!(El instanceof HTMLDialogElement)) { continue; }
                if (El.open !== true || this.Open.has(El) === true) { continue; }
                this.Open.add(El);
                const Entry: kDialogBackEntry = new kDialogBackEntry(El, this);
                El.addEventListener("close", Entry.OnClosed);
                kHashRouter.OpenModal(Entry.Close);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDialogBackWatch.OnMutations");
        }
    };

    public Forget = (El: Element): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Open.delete(El);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDialogBackWatch.Forget");
        }
    };
}

/** One open <dialog>'s history entry: Close is the router's Back (the history already moved), OnClosed the
 *  dialog's own close event (pops the entry unless the router closed it). */
class kDialogBackEntry {
    private Gone: boolean = false;

    constructor(private readonly Dlg: HTMLDialogElement, private readonly Watch: kDialogBackWatch) { }

    public Close = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Gone === true) { return; }
            this.Gone = true;
            if (this.Dlg.open === true) { this.Dlg.close(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDialogBackEntry.Close");
        }
    };

    public OnClosed = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Dlg.removeEventListener("close", this.OnClosed);
            this.Watch.Forget(this.Dlg);
            if (this.Gone === true) { return; }
            this.Gone = true;
            kHashRouter.CloseModal();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDialogBackEntry.OnClosed");
        }
    };
}
